import { ILocation, locationToCoordinates } from "@sp-crm/core";
import { HoveredMapEntity } from "components/community-search/community-map/types";
import { Content, ContentContainer, PageHeader, Stage } from "components/layout";
import { useCommitUserPreference } from "components/manage/account-settings/account-settings-hooks";
import { Subnav } from "components/shared/subnav";
import { ViewMapButton } from "components/shared/view-map-button";
import { SearchRequestPagination } from "components/simple-search/search-request-pagination";
import { PanelType } from "components/ui/panel/panel-type";
import { ResponsivePanel } from "components/ui/panel/responsive-panel";
import { Toggle } from "components/ui/toggle";
import {
    LayoutSectionParentKey,
    ReferenceOrganizationSearchQuery,
    useGetLayoutContainersByKeyQuery,
    useReferenceContactSearchQuery,
    useReferenceOrganizationSearchQuery,
} from "generated/graphql";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { navigate } from "store/actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { ResponsiveMode } from "store/reducers/responsive";
import { usePreferences, useRegionId, useResponsiveMode } from "store/selectors/hooks";
import {
    ReferenceDashboardAspect,
    referenceDashboardSlice,
    ReferenceFilter,
} from "store/slices/reference-dashboard";
import { AddNewContactButton } from "./add-new-contact-button";
import { AddNewOrganizationButton } from "./add-new-organization-button";
import { ReferenceSearchBar } from "./dashboard-reference/reference-search-bar";
import { ReferenceContactSearchList } from "./reference-contact-search-list";
import { ReferenceOrganizationSearchList } from "./reference-organization-search-list";
import {
    ReferenceContactSearchResultsMap,
    ReferenceOrganizationSearchResultsMap,
} from "./reference-search-results-map";

type ReferenceDashboardMapRouteMatch = {
    params?: {
        subpage?: string;
    };
} | null;

export const referralMapThreshold: ResponsiveMode = ResponsiveMode.xLarge;

export const ReferenceDashboardMap: React.FC<unknown> = () => {
    const regionId = useRegionId();
    const dispatch = useAppDispatch();
    const newAppState = useAppSelector(state => state.referenceDashboard);
    const [isContactMapOpen, setIsContactMapOpen] = useState(false);
    const [isOrganizationMapOpen, setIsOrganizationMapOpen] = useState(false);
    const {
        updateSearch,
        changePage,
        changePageSize,
        changeSortWithDirection,
        setExplicitMapBounds,
        resetMapBounds,
        updatePersistence,
        updateRegion,
    } = referenceDashboardSlice.actions;

    const layoutContainersForQuestions = useGetLayoutContainersByKeyQuery({
        keys: [
            LayoutSectionParentKey.ReferenceContact,
            LayoutSectionParentKey.ReferenceBusiness,
            LayoutSectionParentKey.ReferralCommunityCard,
        ],
    });

    const layoutQuestionIds = useMemo(() => {
        return (
            layoutContainersForQuestions.data?.getLayoutContainersByKey
                .flatMap(c =>
                    c.layoutSections.flatMap(s => s.layoutItems.map(i => i.questionId)),
                )
                .filter(x => x) || []
        );
    }, [layoutContainersForQuestions.data]);

    const onSearchUpdated = useCallback(
        (search: ReferenceFilter) => {
            dispatch(updateSearch(search));
        },
        [dispatch, updateSearch],
    );

    const handleContactPageChange = useCallback(
        (page: number) => dispatch(changePage({ aspect: "contact", page })),
        [dispatch, changePage],
    );

    const handleContactPageSizeChange = useCallback(
        (page: number) => dispatch(changePageSize({ aspect: "contact", pageSize: page })),
        [dispatch, changePageSize],
    );

    const handleOrganizationPageChange = useCallback(
        (page: number) => dispatch(changePage({ aspect: "organization", page })),
        [dispatch, changePage],
    );

    const handleOrganizationPageSizeChange = useCallback(
        (page: number) =>
            dispatch(changePageSize({ aspect: "organization", pageSize: page })),
        [dispatch, changePageSize],
    );

    const mapSearchMode: boolean = !!newAppState.contactSearchRequest.geoParams?.bounds;

    const referenceContactSearchQuery = useReferenceContactSearchQuery(
        {
            search: {
                regionId,
                ...newAppState.contactSearchRequest,
                page: mapSearchMode ? 0 : newAppState.contactSearchRequest.page,
                perPage: mapSearchMode ? 9999 : newAppState.contactSearchRequest.perPage,
                includeQuestionIds: layoutQuestionIds,
            },
        },
        { keepPreviousData: true },
    );

    const referenceOrganizationSearchQuery = useReferenceOrganizationSearchQuery(
        {
            search: {
                regionId,
                ...newAppState.organizationSearchRequest,
                includeQuestionIds: layoutQuestionIds,
            },
        },
        { keepPreviousData: true },
    );

    const handleSubpageSelected = useCallback((subpage: string) => {
        navigate(`/references/${subpage}`);
    }, []);

    const handleCloseSection = useCallback(() => {
        navigate(`/references`);
    }, []);

    const routeMatch: ReferenceDashboardMapRouteMatch =
        useRouteMatch(`/references/:subpage`);

    const handleExplicitBoundsChanged = useCallback(
        (northWest: ILocation, southEast: ILocation) => {
            dispatch(
                setExplicitMapBounds({
                    northWest: locationToCoordinates(northWest),
                    southEast: locationToCoordinates(southEast),
                }),
            );
        },
        [dispatch, setExplicitMapBounds],
    );

    const handleResetMapView = useCallback(() => {
        dispatch(resetMapBounds());
    }, [dispatch, resetMapBounds]);

    const handleOpenContactMap = useCallback(() => {
        setIsContactMapOpen(true);
    }, []);

    const handleCloseContactMap = useCallback(() => {
        setIsContactMapOpen(false);
    }, []);

    const handleOpenOrganizationMap = useCallback(() => {
        setIsOrganizationMapOpen(true);
    }, []);

    const handleCloseOrganizationMap = useCallback(() => {
        setIsOrganizationMapOpen(false);
    }, []);

    const contactHits = useMemo(() => {
        if (
            mapSearchMode &&
            referenceContactSearchQuery.data?.referenceContactSearch.hits.length > 0
        ) {
            return referenceContactSearchQuery.data?.referenceContactSearch.hits.slice(
                newAppState.contactSearchRequest.page *
                    newAppState.contactSearchRequest.perPage,
                newAppState.contactSearchRequest.page *
                    newAppState.contactSearchRequest.perPage +
                    newAppState.contactSearchRequest.perPage,
            );
        }

        return referenceContactSearchQuery.data?.referenceContactSearch.hits || [];
    }, [
        mapSearchMode,
        referenceContactSearchQuery.data?.referenceContactSearch.hits,
        newAppState.contactSearchRequest.page,
        newAppState.contactSearchRequest.perPage,
    ]);

    const [hoveredOrganization, setHoveredOrganization] =
        React.useState<HoveredMapEntity | null>(null);

    const responsiveMode = useResponsiveMode();

    const showMap = responsiveMode >= referralMapThreshold;

    const handleSortChange = useCallback(
        (
            aspect: ReferenceDashboardAspect,
            sort: string,
            sortDirection: "ASC" | "DESC",
        ) => {
            dispatch(
                changeSortWithDirection({
                    aspect,
                    sort,
                    direction: sortDirection,
                }),
            );
        },
        [changeSortWithDirection, dispatch],
    );

    const sortValues = useMemo(
        () => ({
            contact: {
                sort: newAppState.contactSearchRequest.sort,
                sortDirection: newAppState.contactSearchRequest.sortDirection,
            },
            business: {
                sort: newAppState.businessSearchRequest.sort,
                sortDirection: newAppState.businessSearchRequest.sortDirection,
            },
            community: {
                sort: newAppState.communitySearchRequest.sort,
                sortDirection: newAppState.communitySearchRequest.sortDirection,
            },
            organization: {
                sort: newAppState.organizationSearchRequest.sort,
                sortDirection: newAppState.organizationSearchRequest.sortDirection,
            },
        }),
        [
            newAppState.contactSearchRequest.sort,
            newAppState.contactSearchRequest.sortDirection,
            newAppState.businessSearchRequest.sort,
            newAppState.businessSearchRequest.sortDirection,
            newAppState.communitySearchRequest.sort,
            newAppState.communitySearchRequest.sortDirection,
            newAppState.organizationSearchRequest.sort,
            newAppState.organizationSearchRequest.sortDirection,
        ],
    );

    const renderContactsTable = useCallback(() => {
        return (
            <>
                <div className="pb-20 lg:pb-0">
                    <div className="w-full flex">
                        <div className="flex-1 mr-4">
                            <ReferenceContactSearchList
                                contactSearchResults={contactHits}
                                setHoveredOrganization={setHoveredOrganization}
                                total={
                                    referenceContactSearchQuery.data
                                        ?.referenceContactSearch.total || 0
                                }
                                sortValues={sortValues}
                                onSortChange={handleSortChange}
                            />
                            {referenceContactSearchQuery.data ? (
                                <SearchRequestPagination
                                    request={newAppState.contactSearchRequest}
                                    total={
                                        referenceContactSearchQuery.data
                                            .referenceContactSearch.total
                                    }
                                    onPageChange={handleContactPageChange}
                                    onPageSizeChange={handleContactPageSizeChange}
                                />
                            ) : null}
                        </div>
                        {showMap ? (
                            <ReferenceContactSearchResultsMap
                                hoveredOrganization={hoveredOrganization}
                                searchParams={newAppState.contactSearchRequest}
                                regionId={regionId}
                                onExplicitBoundsChanged={handleExplicitBoundsChanged}
                            />
                        ) : null}
                    </div>
                    {!showMap ? <ViewMapButton onClick={handleOpenContactMap} /> : null}
                    <ResponsivePanel
                        isOpen={isContactMapOpen}
                        type={PanelType.extraLarge}
                        headerText="Contact Map View"
                        noPadding={true}
                        onDismiss={handleCloseContactMap}>
                        <div className="h-full">
                            <ReferenceContactSearchResultsMap
                                hoveredOrganization={hoveredOrganization}
                                searchParams={newAppState.contactSearchRequest}
                                regionId={regionId}
                                onExplicitBoundsChanged={handleExplicitBoundsChanged}
                            />
                        </div>
                    </ResponsivePanel>
                </div>
            </>
        );
    }, [
        referenceContactSearchQuery.data,
        contactHits,
        newAppState.contactSearchRequest,
        handleContactPageChange,
        handleContactPageSizeChange,
        handleExplicitBoundsChanged,
        regionId,
        hoveredOrganization,
        showMap,
        sortValues,
        handleSortChange,
        handleOpenContactMap,
        isContactMapOpen,
        handleCloseContactMap,
    ]);

    const organizationResults: ReferenceOrganizationSearchQuery["referenceOrganizationSearch"] =
        useMemo(() => {
            if (newAppState.contactSearchRequest.contactCondition) {
                return {
                    hits: [],
                    total: 0,
                };
            }

            return (
                referenceOrganizationSearchQuery.data?.referenceOrganizationSearch || {
                    hits: [],
                    total: 0,
                }
            );
        }, [
            referenceOrganizationSearchQuery.data?.referenceOrganizationSearch,
            newAppState.contactSearchRequest.contactCondition,
        ]);

    const organizationHits = useMemo(() => {
        if (mapSearchMode && organizationResults.hits.length > 0) {
            return organizationResults.hits.slice(
                newAppState.organizationSearchRequest.page *
                    newAppState.organizationSearchRequest.perPage,
                newAppState.organizationSearchRequest.page *
                    newAppState.organizationSearchRequest.perPage +
                    newAppState.organizationSearchRequest.perPage,
            );
        }

        return organizationResults.hits || [];
    }, [
        mapSearchMode,
        organizationResults.hits,
        newAppState.organizationSearchRequest.page,
        newAppState.organizationSearchRequest.perPage,
    ]);

    const renderOrganizationsTable = useCallback(() => {
        return (
            <>
                <div className="pb-20 lg:pb-0">
                    <div className="w-full flex">
                        <div className="flex-1 mr-4">
                            <ReferenceOrganizationSearchList
                                organizationSearchResults={organizationHits}
                                setHoveredOrganization={setHoveredOrganization}
                                total={organizationResults.total}
                                sortValues={sortValues}
                                onSortChange={handleSortChange}
                            />
                            {referenceOrganizationSearchQuery.data ? (
                                <SearchRequestPagination
                                    request={newAppState.organizationSearchRequest}
                                    total={organizationResults.total}
                                    onPageChange={handleOrganizationPageChange}
                                    onPageSizeChange={handleOrganizationPageSizeChange}
                                />
                            ) : null}
                        </div>
                        {showMap ? (
                            <ReferenceOrganizationSearchResultsMap
                                hoveredOrganization={hoveredOrganization}
                                searchParams={newAppState.organizationSearchRequest}
                                regionId={regionId}
                                onExplicitBoundsChanged={handleExplicitBoundsChanged}
                            />
                        ) : null}
                    </div>
                    {!showMap ? (
                        <ViewMapButton onClick={handleOpenOrganizationMap} />
                    ) : null}
                    <ResponsivePanel
                        isOpen={isOrganizationMapOpen}
                        type={PanelType.extraLarge}
                        headerText="Organization Map View"
                        noPadding={true}
                        onDismiss={handleCloseOrganizationMap}>
                        <div className="h-full">
                            <ReferenceOrganizationSearchResultsMap
                                hoveredOrganization={hoveredOrganization}
                                searchParams={newAppState.organizationSearchRequest}
                                regionId={regionId}
                                onExplicitBoundsChanged={handleExplicitBoundsChanged}
                            />
                        </div>
                    </ResponsivePanel>
                </div>
            </>
        );
    }, [
        organizationHits,
        organizationResults.total,
        handleOrganizationPageChange,
        handleOrganizationPageSizeChange,
        newAppState.organizationSearchRequest,
        referenceOrganizationSearchQuery.data,
        setHoveredOrganization,
        regionId,
        handleExplicitBoundsChanged,
        hoveredOrganization,
        showMap,
        sortValues,
        handleSortChange,
        handleOpenOrganizationMap,
        isOrganizationMapOpen,
        handleCloseOrganizationMap,
    ]);

    const subpages = [
        {
            href: `/references/contacts`,
            linkText: `Contacts`,
            badgeCount: referenceContactSearchQuery.data?.referenceContactSearch.total,
            subpage: "contacts",
            render: renderContactsTable,
        },
        {
            href: `/references/organizations`,
            linkText: `Organizations`,
            badgeCount: organizationResults.total,
            subpage: "organizations",
            render: renderOrganizationsTable,
        },
    ];

    const { referralMapExperience, persistReferralSearch } = usePreferences();

    const commitMap = useCommitUserPreference("referralMapExperience");

    useEffect(() => {
        dispatch(updatePersistence(persistReferralSearch ? "on" : "off"));
    }, [dispatch, updatePersistence, persistReferralSearch]);
    useEffect(() => {
        dispatch(updateRegion(regionId));
    }, [dispatch, updateRegion, regionId]);

    return (
        <Stage>
            <PageHeader title="Referral sources">
                <div className="flex items-center space-x-2">
                    <AddNewContactButton />
                    <AddNewOrganizationButton />
                </div>
            </PageHeader>
            <Content>
                <ReferenceSearchBar
                    value={newAppState.searchFilter}
                    onChange={onSearchUpdated}
                    onResetMapView={referralMapExperience ? handleResetMapView : null}
                />
            </Content>
            <ContentContainer className="bg-zinc-50 mx-0! mt-4 px-4 py-2">
                <div>
                    <Subnav
                        subpages={subpages}
                        navigate={handleSubpageSelected}
                        selectedSubpage={routeMatch?.params?.subpage}
                        defaultSubpage="contacts"
                        closeSection={handleCloseSection}
                        widthMode="auto"
                        headerComponent={
                            showMap ? (
                                <Toggle
                                    label="Use map experience"
                                    checked={true}
                                    onChange={newValue => {
                                        commitMap(newValue);
                                    }}
                                />
                            ) : null
                        }
                    />
                </div>
            </ContentContainer>
        </Stage>
    );
};
